import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Home, Maximize2, BedDouble, Bath, Car, FileText, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { PROPERTY } from "../constants/testIds";

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, authAxios } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryData, setInquiryData] = useState({ name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(), phone: "", message: "" });
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const language = user?.language || "en";

  useEffect(() => {
    authAxios.get(`/user/property/${id}`)
      .then(({ data }) => setProperty(data))
      .catch((err) => { if (err.response?.status === 401) logout(); else navigate("/dashboard"); })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    setInquiryLoading(true);
    try {
      await authAxios.post("/user/inquiry", { property_id: id, ...inquiryData });
      setInquirySubmitted(true);
    } catch (err) { console.error("Inquiry error:", err); }
    finally { setInquiryLoading(false); }
  };

  const t = (en, it) => language === "it" && it ? it : en;

  // Computed values — must be before early returns to satisfy hook rules
  const locationText = useMemo(
    () => [property?.city, property?.region, property?.country].filter(Boolean).join(", "),
    [property?.city, property?.region, property?.country]
  );

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-px h-16 bg-[#C8102E] animate-pulse" />
    </div>
  );

  if (!property) return null;

  const images = [...new Set([property.featured_image, ...(property.images || [])].filter(Boolean))];
  const name = t(property.name_en, property.name_it);
  const description = t(property.description_en, property.description_it);
  const highlights = t(property.highlights_en, property.highlights_it);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={PROPERTY.detailContainer}>
      {/* Nav */}
      <div className="fixed top-0 left-0 right-0 z-50 px-8 py-5 flex items-center justify-between bg-[#050505]/90 backdrop-blur-sm border-b border-zinc-900/60">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 text-white hover:text-white transition-colors group">
          <ArrowLeft size={14} strokeWidth={1.5} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-light">Back</span>
        </button>
        <img
          src="https://customer-assets.emergentagent.com/job_investment-portal-38/artifacts/5nykqmge_Green%20and%20Gold%20Professional%20Letter%20A%20Logo.png"
          alt="Scarlett Properties"
          className="h-9 w-auto"
          style={{ mixBlendMode: "screen" }}
        />
      </div>

      {/* Hero Gallery */}
      <div className="relative pt-14" data-testid={PROPERTY.heroImage}>
        <div className="relative h-[55vh] md:h-[70vh] bg-zinc-900 overflow-hidden">
          {images.length > 0 ? (
            <motion.img
              key={activeImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              src={images[activeImage]}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-12 h-px bg-zinc-800" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

          {/* Gallery thumbnails */}
          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((img, i) => (
                <button key={img} onClick={() => setActiveImage(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeImage ? "bg-white w-4" : "bg-white/30"}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left */}
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="w-6 h-px bg-[#C8102E] mb-6" />
              {property.category_name && (
                <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-3 font-light">{property.category_name}</p>
              )}
              <h1 className="font-serif text-4xl md:text-5xl font-light text-white tracking-tight mb-4">{name}</h1>
              <div className="flex items-center gap-2 mb-10">
                <MapPin size={11} strokeWidth={1.5} className="text-white" />
                <span className="text-xs text-white tracking-wider font-light">
                  {locationText}
                </span>
              </div>

              {description && (
                <div className="mb-12">
                  <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-5 font-light">Overview</p>
                  <div className="text-white text-sm leading-loose font-light whitespace-pre-wrap">{description}</div>
                </div>
              )}

              {highlights && (
                <div className="mb-12">
                  <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-5 font-light">Investment Highlights</p>
                  <div className="border-l border-[#C8102E]/30 pl-5">
                    <div className="text-white text-sm leading-loose font-light whitespace-pre-wrap">{highlights}</div>
                  </div>
                </div>
              )}

              {/* Map */}
              {(property.maps_embed_url || (property.latitude && property.longitude)) && (
                <div className="mb-12">
                  <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-5 font-light">Location</p>
                  {property.maps_embed_url ? (
                    <div className="aspect-video bg-zinc-900 overflow-hidden">
                      <iframe src={property.maps_embed_url} width="100%" height="100%" frameBorder="0" style={{ border: 0 }} allowFullScreen title="map" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-zinc-900 flex items-center justify-center">
                      <p className="text-white text-xs tracking-wider">
                        {property.latitude}, {property.longitude}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Image gallery */}
              {images.length > 1 && (
                <div className="grid grid-cols-2 gap-2">
                  {images.slice(1, 5).map((img) => (
                    <button key={img} onClick={() => setActiveImage(images.indexOf(img))} className="aspect-video overflow-hidden bg-zinc-900 group">
                      <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-70 hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right — sticky data sheet */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}>
                {/* Price */}
                {property.price_visibility !== "hidden" && (
                  <div className="mb-8 pb-8 border-b border-zinc-800">
                    <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-2 font-light">Investment</p>
                    <p className="font-serif text-2xl font-light text-white">
                      {property.price_visibility === "on_request" || !property.price
                        ? "Price on Request"
                        : `${property.currency} ${Number(property.price).toLocaleString()}`
                      }
                    </p>
                  </div>
                )}

                {/* Metrics */}
                <div className="mb-8 pb-8 border-b border-zinc-800">
                  <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-5 font-light">Property Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    {property.living_area && <Metric icon={<Maximize2 size={12} strokeWidth={1.5} />} label="Living Area" value={`${property.living_area} m²`} />}
                    {property.land_area && <Metric icon={<Home size={12} strokeWidth={1.5} />} label="Land Area" value={`${property.land_area} m²`} />}
                    {property.bedrooms && <Metric icon={<BedDouble size={12} strokeWidth={1.5} />} label="Bedrooms" value={property.bedrooms} />}
                    {property.bathrooms && <Metric icon={<Bath size={12} strokeWidth={1.5} />} label="Bathrooms" value={property.bathrooms} />}
                    {property.parking_spaces && <Metric icon={<Car size={12} strokeWidth={1.5} />} label="Parking" value={property.parking_spaces} />}
                    {property.construction_year && <Metric icon={null} label="Built" value={property.construction_year} />}
                    {property.renovation_year && <Metric icon={null} label="Renovated" value={property.renovation_year} />}
                    {property.property_type && <Metric icon={null} label="Type" value={property.property_type} />}
                  </div>
                </div>

                {/* Reference */}
                {property.internal_ref && (
                  <div className="mb-8 pb-8 border-b border-zinc-800">
                    <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-2 font-light">Reference</p>
                    <p className="font-mono text-white text-sm tracking-widest">{property.internal_ref}</p>
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={() => setShowInquiry(true)}
                  data-testid={PROPERTY.inquiryBtn}
                  className="w-full bg-[#C8102E] hover:bg-[#A50D25] text-white text-[10px] tracking-[0.4em] uppercase py-5 transition-colors duration-300 font-light"
                >
                  Request a Confidential Discussion
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      <Dialog open={showInquiry} onOpenChange={(o) => { setShowInquiry(o); if (!o) setTimeout(() => { setInquirySubmitted(false); }, 300); }}>
        <DialogContent className="bg-[#080808] border border-zinc-800 text-white max-w-sm p-8" data-testid={PROPERTY.inquiryModal}>
          <DialogHeader className="mb-6">
            <div className="w-6 h-px bg-[#C8102E] mb-5" />
            <DialogTitle className="font-serif text-2xl font-light text-white tracking-wide">Confidential Discussion</DialogTitle>
            <DialogDescription className="text-xs text-white/70 tracking-wide mt-2 font-light">{name}</DialogDescription>
          </DialogHeader>
          {!inquirySubmitted ? (
            <form onSubmit={handleInquiry} className="space-y-6">
              <input type="text" value={inquiryData.name} onChange={e => setInquiryData(d => ({...d, name: e.target.value}))} placeholder="Full Name" data-testid={PROPERTY.inquiryName} required className="w-full bg-transparent border-b border-zinc-800 text-white py-2 text-sm placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500 transition-colors font-light" />
              <input type="tel" value={inquiryData.phone} onChange={e => setInquiryData(d => ({...d, phone: e.target.value}))} placeholder="Mobile Number" data-testid={PROPERTY.inquiryPhone} required className="w-full bg-transparent border-b border-zinc-800 text-white py-2 text-sm placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500 transition-colors font-light" />
              <textarea value={inquiryData.message} onChange={e => setInquiryData(d => ({...d, message: e.target.value}))} placeholder="Message (optional)" data-testid={PROPERTY.inquiryMessage} rows={3} className="w-full bg-transparent border-b border-zinc-800 text-white py-2 text-sm placeholder:text-zinc-700 focus:outline-none focus:border-zinc-500 transition-colors font-light resize-none" />
              <button type="submit" disabled={inquiryLoading} data-testid={PROPERTY.inquirySubmit} className="w-full bg-[#C8102E] hover:bg-[#A50D25] text-white text-[10px] tracking-[0.4em] uppercase py-4 transition-colors duration-300 disabled:opacity-50 font-light mt-2">
                {inquiryLoading ? "Sending..." : "Submit"}
              </button>
            </form>
          ) : (
            <div className="py-8 text-center space-y-3">
              <div className="w-8 h-px bg-[#C8102E] mx-auto" />
              <p className="text-white text-sm tracking-wider font-light">Your inquiry has been received.</p>
              <p className="text-white/70 text-xs tracking-wider font-light">An advisor will contact you shortly.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-white">{icon}<span className="text-[9px] tracking-[0.25em] uppercase font-light">{label}</span></div>
      <span className="text-white text-sm font-light">{value}</span>
    </div>
  );
}
